import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaUsers,
  FaClock
} from 'react-icons/fa';

const PageContainer = styled.div`
  margin-top: 180px;
  min-height: calc(100vh - 210px);
  background: #ffffff;


  @media (max-width: 768px) {
    margin-top: 120px;
  }
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, #3730A3 0%, #0b0b77 50%, #1e1b4b 100%);
  color: white;
  padding: 4rem 5%;
  text-align: center;
  
  h1 {
    font-size: 3rem;
    margin-bottom: 1rem;
    
    span {
      color: #ffd966;
    }
  }
  
  p {
    font-size: 1.2rem;
    max-width: 600px;
    margin: 0 auto;
    opacity: 0.9;
  }
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 5%;
`;

const Section = styled.section`
  background: #ffffff;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  color: #3730A3;
  font-weight: 700;
  text-align: center;
  margin-bottom: 0.5rem;

  span {
    color: #ffd966;
  }
`;

const Subtitle = styled.p`
  text-align: center;
  color: #4a4a6a;
  max-width: 650px;
  margin: 0 auto 3rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.8rem;
  
  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(Link)`
  position: relative;
  overflow: hidden;
  border-radius: 18px;
  text-decoration: none;
  background-image: url(${props => props.bg});
  background-size: cover;
  background-position: center;
  box-shadow: 0 15px 30px rgba(55, 48, 163, 0.1);
  transition: 0.45s;
  min-height: 300px;
  width: 100%;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 50px rgba(55, 48, 163, 0.2);
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(11, 11, 119, 0.92),
    rgba(55, 48, 163, 0.7),
    rgba(55, 48, 163, 0.3)
  );
`;

const Content = styled.div`
  position: relative;
  z-index: 2;
  padding: 2rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 300px;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  color: white;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const CardDesc = styled.p`
  color: #d4d0f0;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  flex: 1;
`;

const Meta = styled.div`
  display: flex;
  gap: 1.2rem;
  margin-bottom: 1rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #ffd966;
  font-size: 0.8rem;
`;

const Button = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #ffd966;
  padding: 0.6rem 1.2rem;
  border-radius: 40px;
  font-weight: 600;
  font-size: 0.9rem;
  color: #3730A3;
  width: fit-content;
  transition: 0.3s;

  svg {
    transition: 0.3s;
    font-size: 0.8rem;
    color: #3730A3;
  }

  &:hover {
    background: #ffffff;
    box-shadow: 0 8px 25px rgba(255, 217, 102, 0.3);
  }

  ${Card}:hover & svg {
    transform: translateX(7px);
  }
`;

const courses = [
  {
    title: "Medical Coding Certification",
    desc: "Comprehensive training in ICD-10-CM, ICD-10-PCS, CPT, and HCPCS coding systems with real-world scenarios.",
    students: "2.5K Students",
    duration: "60 Hours",
    path: "/coding",
    bg: "/images/medicalcodingcertification.png"
  },
  {
    title: "Inpatient DRG Coding",
    desc: "Master DRG assignment, POA indicators, and MS-DRG methodologies for inpatient coding.",
    students: "1.8K Students",
    duration: "50 Hours",
    path: "/inpatient-drg",
    bg: "/images/inpatientdrg.jpg"
  },
  {
    title: "ICD-10-CM Diagnosis",
    desc: "Complete diagnosis coding training for all healthcare settings with official guidelines.",
    students: "3K Students",
    duration: "40 Hours",
    path: "/icd-10-cm",
    bg: "/images/icd10cm.jpg"
  },
  {
    title: "ICD-10-PCS Procedure",
    desc: "Master inpatient procedure coding with 31 root operations and practical applications.",
    students: "1.5K Students",
    duration: "45 Hours",
    path: "/icd-10-pcs",
    bg: "/images/icd10pcs.jpg"
  },
  {
    title: "CPT & E/M Coding",
    desc: "Current Procedural Terminology and Evaluation & Management coding mastery.",
    students: "2K Students",
    duration: "50 Hours",
    path: "/cpt-e-m",
    bg: "/images/cpt-em.webp"
  },
  {
    title: "Exam Preparation",
    desc: "Intensive preparation for CCS, CPC, CIC, and other certification exams.",
    students: "3.5K Students",
    duration: "40 Hours",
    path: "/exam-prep",
    bg: "/images/examprep.jpg"
  },
  {
    title: "Cardiovascular Coding",
    desc: "Specialized training in cardiac procedures, diagnostics, and interventions.",
    students: "1.2K Students",
    duration: "40 Hours",
    path: "/specialty/cardiovascular",
    bg: "/images/cardiovascular.jpg"
  },
  {
    title: "Orthopedic Coding",
    desc: "Master fracture care, arthroscopy, arthroplasty, and spinal procedure coding.",
    students: "1.1K Students",
    duration: "40 Hours",
    path: "/specialty/orthopedic",
    bg: "/images/orthopedic.jpg"
  },
  {
    title: "CDI Training",
    desc: "Clinical Documentation Improvement with query writing and CDI program management.",
    students: "850 Students",
    duration: "50 Hours",
    path: "/cdi-training",
    bg: "/images/cdi.jpg"
  },
  {
    title: "HCC/Risk Adjustment",
    desc: "Master Hierarchical Condition Categories, RAF scores, and risk adjustment models.",
    students: "1.5K Students",
    duration: "45 Hours",
    path: "/hcc-risk-adjustment",
    bg: "/images/hcc.jpeg"
  },
  {
    title: "ED & E/M Training",
    desc: "Emergency Department coding and Evaluation & Management services mastery.",
    students: "950 Students",
    duration: "35 Hours",
    path: "/specialty/ed-em-training",
    bg: "/images/ed-em.webp"
  },
  {
    title: "Auditing Training",
    desc: "Professional medical auditing with compliance and CPMA certification preparation.",
    students: "900 Students",
    duration: "45 Hours",
    path: "/auditing",
    bg: "/images/auditing.jpg"
  },
  {
    title: "ICD-11 Coding",
    desc: "Next-generation diagnosis coding with updated scientific content.",
    students: "500 Students",
    duration: "35 Hours",
    path: "/icd-11",
    bg: "/images/icd11.jfif"
  },
  {
    title: "Radiation Oncology",
    desc: "Complete radiation therapy coding including IMRT, SBRT, and brachytherapy.",
    students: "600 Students",
    duration: "30 Hours",
    path: "/specialty/radiation-oncology",
    bg: "/images/radiation.jpg"
  }
];

const AllCourses = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <PageContainer>
      <HeroSection>
        <h1>All <span>Training Programs</span></h1>
        <p>Explore our complete range of medical coding and healthcare training programs</p>
      </HeroSection>

      <Container>
        <Section>
          <Title>Our <span>Complete Course Catalog</span></Title>
          <Subtitle>
            Comprehensive medical coding and healthcare programs designed for real-world careers
          </Subtitle>

          <Grid>
            {courses.map((course, index) => (
              <Card
                key={index}
                to={course.path}
                bg={course.bg}
              >
                <Overlay />
                <Content>
                  <div>
                    <CardTitle>{course.title}</CardTitle>
                    <CardDesc>{course.desc}</CardDesc>
                  </div>
                  <div>
                    <Meta>
                      <MetaItem>
                        <FaUsers /> {course.students}
                      </MetaItem>
                      <MetaItem>
                        <FaClock /> {course.duration}
                      </MetaItem>
                    </Meta>
                    <Button>
                      Learn More <FaArrowRight />
                    </Button>
                  </div>
                </Content>
              </Card>
            ))}
          </Grid>
        </Section>
      </Container>
    </PageContainer>
  );
};

export default AllCourses;